import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { User, Mail, Phone, Calendar, Globe, ChefHat, Coffee, Utensils, Clock, Wine, Flame, AlertCircle, Camera, Save, Loader } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LocationSearch from '../components/LocationSearch';
import { cuisineOptions, dietaryOptions, languageOptions, interestOptions } from '../data/filterOptions';

const ProfileCompletion = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);
  const [step, setStep] = useState(1);
  const totalSteps = 4;
  
  const [formData, setFormData] = useState({
    fullName: '',
    contactMethod: 'email', // 'email' or 'phone'
    phone: '',
    dateOfBirth: '',
    location: '',
    location_lat: null as number | null,
    location_lng: null as number | null,
    bio: '',
    favoriteCuisines: [] as string[],
    dietaryPreferences: [] as string[],
    priceRange: '',
    preferredDiningTimes: [] as string[],
    languages: [] as string[],
    interests: [] as string[],
    diningStyle: '',
    drinkingPreference: '',
    smokingPreference: '',
    privacySettings: {
      profile_visibility: 'public',
      show_age: true,
      show_location: true,
      show_interests: true,
      show_languages: true,
      show_smoking_preference: true,
      show_dietary_preferences: true,
      show_drinking_preference: true
    }
  });

  const diningTimeOptions = [
    'Früher Morgen (06:00-08:00)', 'Frühstück (08:00-10:00)',
    'Brunch (10:00-12:00)', 'Mittagessen (12:00-14:00)',
    'Nachmittag (14:00-17:00)', 'Frühes Abendessen (17:00-19:00)',
    'Abendessen (19:00-21:00)', 'Später Abend (21:00+)'
  ];

  const diningStyleOptions = [
    { value: 'casual', label: 'Casual' },
    { value: 'fine_dining', label: 'Fine Dining' },
    { value: 'social', label: 'Sozial' },
    { value: 'adventurous', label: 'Erlebnisdinner' }
  ];

  const drinkingPreferenceOptions = [
    { value: 'non_drinker', label: 'Nicht-Trinker' },
    { value: 'social_drinker', label: 'Gesellschaftstrinker' },
    { value: 'wine_enthusiast', label: 'Wein Liebhaber' },
    { value: 'cocktail_lover', label: 'Cocktail Enthusiast' },
    { value: 'beer_enthusiast', label: 'Bier Liebhaber' }
  ];

  const smokingPreferenceOptions = [
    { value: 'non_smoker', label: 'Nichtraucher' },
    { value: 'social_smoker', label: 'Party Raucher' },
    { value: 'regular_smoker', label: 'Raucher' },
    { value: 'vaper', label: 'Vaper' }
  ];

  const privacyLabels: Record<string, string> = {
    show_age: 'Alter anzeigen',
    show_location: 'Standort anzeigen',
    show_languages: 'Sprachen anzeigen',
    show_interests: 'Interessen anzeigen',
    show_dietary_preferences: 'Essgewohnheiten anzeigen',
    show_drinking_preference: 'Trinkverhalten anzeigen',
    show_smoking_preference: 'Rauchverhalten anzeigen',
  };

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session }, error: authError } = await supabase.auth.getSession();
      if (authError) throw authError;

      if (!session?.user) {
        if (!session?.user) {
          navigate('/login');
          return;
        }
      } else {
        setUser(session.user);
      }

      // Check if user already has a profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();
          
      if (profileError && profileError.code !== 'PGRST116') {
        throw profileError;
      }
      
      // If profile exists and has required fields, pre-fill the form
      if (profile) {
        setFormData({
          fullName: profile.full_name || session?.user.user_metadata?.full_name || '',
          contactMethod: profile.phone ? 'phone' : 'email',
          phone: profile.phone || '',
          dateOfBirth: profile.date_of_birth || '',
          location: profile.location || '',
          location_lat: profile.location_lat,
          location_lng: profile.location_lng,
          bio: profile.bio || '',
          favoriteCuisines: profile.favorite_cuisines || [],
          dietaryPreferences: profile.dietary_preferences || [],
          priceRange: profile.price_range || '',
          preferredDiningTimes: profile.preferred_dining_times || [],
          languages: profile.languages || [],
          interests: profile.interests || [],
          diningStyle: profile.dining_style || '',
          drinkingPreference: profile.drinking_preference || '',
          smokingPreference: profile.smoking_preference || '',
          privacySettings: profile.privacy_settings || {
            profile_visibility: 'public',
            show_age: true,
            show_location: true,
            show_interests: true,
            show_languages: true,
            show_smoking_preference: true,
            show_dietary_preferences: true,
            show_drinking_preference: true
          }
        });
        
        // Set profile picture preview if it exists
        if (profile.profile_picture) {
          setProfilePicturePreview(profile.profile_picture);
        }
        
        // If profile is already complete, redirect to home
        if (profile.full_name && profile.location && profile.bio) {
          navigate('/');
          return;
        }
      } else {
        // Pre-fill with data from OAuth provider if available
        setFormData(prev => ({
          ...prev,
          fullName: session.user.user_metadata?.full_name || '',
          contactMethod: 'email',
        }));
      }
    } catch (err) {
      console.error('Error checking auth:', err);
      setError(err instanceof Error ? err.message : 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleLocationSelect = (location: { address: string; lat: number; lng: number }) => {
    setFormData(prev => ({
      ...prev,
      location: location.address,
      location_lat: location.lat,
      location_lng: location.lng
    }));
  };

  const handleCheckboxChange = (name: string, value: string) => {
    setFormData(prev => {
      const currentValues = prev[name as keyof typeof prev] as string[];
      return {
        ...prev,
        [name]: currentValues.includes(value)
          ? currentValues.filter(item => item !== value)
          : [...currentValues, value]
      };
    });
  };

  const handleProfileVisibilityChange = (visibility: string) => {
    setFormData(prev => ({
      ...prev,
      privacySettings: { ...prev.privacySettings, profile_visibility: visibility }
    }));
  };

  const handlePrivacyChange = (key: string) => {
    setFormData(prev => ({
      ...prev,
      privacySettings: {
        ...prev.privacySettings,
        [key]: !prev.privacySettings[key as keyof typeof prev.privacySettings]
      }
    }));
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !user) return;
    
    const file = e.target.files[0];
    if (file.size > 2 * 1024 * 1024) {
      setError('Profile pictures must be under 2MB');
      return;
    }
    
    // Create a preview
    setProfilePicturePreview(URL.createObjectURL(file));
    
    try {
      setUploadingPicture(true);
      setError(null);
      
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}/profile.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('profile-pictures')
        .upload(fileName, file, { upsert: true });
        
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('profile-pictures')
        .getPublicUrl(fileName);
        
      // Update profile with new picture URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ profile_picture: publicUrl })
        .eq('id', user.id);
        
      if (updateError) throw updateError;
      
      setSuccess('Profile picture updated successfully');
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const handleNextStep = () => {
    // Validate current step
    if (step === 1) {
      if (!formData.fullName.trim()) {
        setError('Please enter your full name');
        return;
      }
      
      if (formData.contactMethod === 'phone' && !formData.phone.trim()) {
        setError('Please enter your phone number');
        return;
      }
    }
    
    setError(null);
    setStep(prev => Math.min(prev + 1, totalSteps));
  };

  const handlePrevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Prepare the update data
      const updateData = {
        full_name: formData.fullName,
        phone: formData.contactMethod === 'phone' ? formData.phone : null,
        date_of_birth: formData.dateOfBirth || null,
        location: formData.location,
        location_lat: formData.location_lat,
        location_lng: formData.location_lng,
        bio: formData.bio,
        favorite_cuisines: formData.favoriteCuisines,
        dietary_preferences: formData.dietaryPreferences,
        price_range: formData.priceRange,
        preferred_dining_times: formData.preferredDiningTimes,
        languages: formData.languages,
        interests: formData.interests,
        dining_style: formData.diningStyle,
        drinking_preference: formData.drinkingPreference,
        smoking_preference: formData.smokingPreference,
        privacy_settings: formData.privacySettings,
        updated_at: new Date()
      };
      
      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id);
        
      if (updateError) throw updateError;
      
      setSuccess('Profile updated successfully');
      
      // Redirect to home page after a delay
      setTimeout(() => {
        navigate('/');
      }, 1500);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err instanceof Error ? err.message : 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-6 text-white">
            <h1 className="text-2xl font-bold">Complete Your Profile</h1>
            <p className="mt-2 text-orange-100">
              Tell us more about yourself to get the most out of Meet&Eat
            </p>
          </div>
          
          {/* Progress Bar */}
          <div className="px-6 pt-6">
            <div className="relative pt-1">
              <div className="flex mb-2 items-center justify-between">
                <div>
                  <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-orange-600 bg-orange-200">
                    Step {step} of {totalSteps}
                  </span>
                </div>
                <div className="text-right">
                  <span className="text-xs font-semibold inline-block text-orange-600">
                    {Math.round((step / totalSteps) * 100)}%
                  </span>
                </div>
              </div>
              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-orange-200">
                <div 
                  style={{ width: `${(step / totalSteps) * 100}%` }} 
                  className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-orange-500 transition-all duration-500"
                ></div>
              </div>
            </div>
          </div>
          
          {error && (
            <div className="mx-6 mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
              <AlertCircle className="h-5 w-5 mr-2" />
              <span>{error}</span>
            </div>
          )}
          
          {success && (
            <div className="mx-6 mb-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded flex items-center">
              <Save className="h-5 w-5 mr-2" />
              <span>{success}</span>
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="p-6">
            {/* Step 1: Basic Information */}
            {step === 1 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Basic Information</h2>
                
                {/* Profile Picture */}
                <div className="flex flex-col items-center space-y-4">
                  <div className="relative w-32 h-32">
                    {profilePicturePreview ? (
                      <img
                        src={profilePicturePreview}
                        alt="Profile preview"
                        className="w-full h-full rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center">
                        <User className="h-12 w-12 text-gray-400" />
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingPicture}
                      className="absolute bottom-0 right-0 bg-orange-500 text-white p-2 rounded-full hover:bg-orange-600 transition shadow-lg"
                    >
                      {uploadingPicture ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Camera className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    className="hidden"
                  />
                  <span className="text-sm text-gray-500">
                    Upload profile picture (max 2MB)
                  </span>
                </div>
                
                {/* Full Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4" />
                      <span>Full Name*</span>
                    </div>
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
                
                {/* Contact Method */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Preferred Contact Method
                  </label>
                  <div className="flex space-x-4 mb-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="email"
                        checked={formData.contactMethod === 'email'}
                        onChange={handleInputChange}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <span>Email</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        name="contactMethod"
                        value="phone"
                        checked={formData.contactMethod === 'phone'}
                        onChange={handleInputChange}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <span>Phone</span>
                    </label>
                  </div>
                  
                  {formData.contactMethod === 'phone' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <div className="flex items-center space-x-2">
                          <Phone className="h-4 w-4" />
                          <span>Phone Number*</span>
                        </div>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                        required={formData.contactMethod === 'phone'}
                      />
                    </div>
                  )}
                </div>
                
                {/* Date of Birth */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4" />
                      <span>Date of Birth</span>
                    </div>
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  />
                </div>
                
                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4" />
                      <span>Location*</span>
                    </div>
                  </label>
                  <LocationSearch
                    onLocationSelect={handleLocationSelect}
                    defaultValue={formData.location}
                  />
                </div>
              </div>
            )}
            
            {/* Step 2: Dining Preferences */}
            {step === 2 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Dining Preferences</h2>
                
                {/* Bio */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    About Me
                  </label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    placeholder="Tell us about yourself..."
                  />
                </div>
                
                {/* Favorite Cuisines */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <ChefHat className="h-4 w-4" />
                      <span>Favorite Cuisines</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {cuisineOptions.map((cuisine) => (
                      <label key={cuisine} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.favoriteCuisines.includes(cuisine)}
                          onChange={() => handleCheckboxChange('favoriteCuisines', cuisine)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{cuisine}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Dietary Preferences */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Utensils className="h-4 w-4" />
                      <span>Dietary Preferences</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {dietaryOptions.map((option) => (
                      <label key={option} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.dietaryPreferences.includes(option)}
                          onChange={() => handleCheckboxChange('dietaryPreferences', option)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{option}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Price Range */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Range</label>
                  <select
                    name="priceRange"
                    value={formData.priceRange}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select price range</option>
                    <option value="$">$ (Budget-friendly)</option>
                    <option value="$$">$$ (Moderate)</option>
                    <option value="$$$">$$$ (Upscale)</option>
                    <option value="$$$$">$$$$ (Luxury)</option>
                  </select>
                </div>
                
                {/* Preferred Dining Times */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Clock className="h-4 w-4" />
                      <span>Preferred Dining Times</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {diningTimeOptions.map((time) => (
                      <label key={time} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.preferredDiningTimes.includes(time)}
                          onChange={() => handleCheckboxChange('preferredDiningTimes', time)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{time}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Additional Information */}
            {step === 3 && (
              <div className="space-y-6">
                <h2 className="text-xl font-semibold text-gray-900 border-b pb-2">Additional Information</h2>
                
                {/* Languages */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Globe className="h-4 w-4" />
                      <span>Languages</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {languageOptions.map((language) => (
                      <label key={language} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.languages.includes(language)}
                          onChange={() => handleCheckboxChange('languages', language)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{language}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Interests */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Coffee className="h-4 w-4" />
                      <span>Interests</span>
                    </div>
                  </label>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {interestOptions.map((interest) => (
                      <label key={interest} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.interests.includes(interest)}
                          onChange={() => handleCheckboxChange('interests', interest)}
                          className="rounded text-orange-500 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-600">{interest}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                {/* Dining Style */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dining Style</label>
                  <select
                    name="diningStyle"
                    value={formData.diningStyle}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select dining style</option>
                    {diningStyleOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Drinking Preference */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Wine className="h-4 w-4" />
                      <span>Drinking Preference</span>
                    </div>
                  </label>
                  <select
                    name="drinkingPreference"
                    value={formData.drinkingPreference}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select drinking preference</option>
                    {drinkingPreferenceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Smoking Preference */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center space-x-2">
                      <Flame className="h-4 w-4" />
                      <span>Smoking Preference</span>
                    </div>
                  </label>
                  <select
                    name="smokingPreference"
                    value={formData.smokingPreference}
                    onChange={handleInputChange}
                    className="w-full p-2 border rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="">Select smoking preference</option>
                    {smokingPreferenceOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
            
            {/* Step 4: Privacy Settings */}
            {step === 4 && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Privacy Settings</h3>
                
                {/* Profile Visibility */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Profile Visibility
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={formData.privacySettings.profile_visibility === 'public'}
                        onChange={() => handleProfileVisibilityChange('public')}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <span>Public - Anyone can see your profile</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={formData.privacySettings.profile_visibility === 'friends'}
                        onChange={() => handleProfileVisibilityChange('friends')}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <span>Friends Only - Only accepted friends can see your profile</span>
                    </label>
                    <label className="flex items-center space-x-2">
                      <input
                        type="radio"
                        checked={formData.privacySettings.profile_visibility === 'private'}
                        onChange={() => handleProfileVisibilityChange('private')}
                        className="text-orange-500 focus:ring-orange-500"
                      />
                      <span>Private - Only you can see your profile</span>
                    </label>
                  </div>
                </div>
                
                {/* Information Visibility */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Information Visibility
                  </label>
                  <div className="space-y-2">
                    {Object.entries(formData.privacySettings)
                      .filter(([key]) => key !== 'profile_visibility')
                      .map(([key, value]) => (
                        <label key={key} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={value as boolean}
                            onChange={() => handlePrivacyChange(key)}
                            className="rounded text-orange-500 focus:ring-orange-500"
                          />
                          <span>{privacyLabels[key] ?? key}</span>
                        </label>
                      ))}
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-medium text-blue-700 mb-2">Privacy Information</h3>
                  <p className="text-sm text-blue-600">
                    Your privacy is important to us. You can change these settings at any time from your profile settings page.
                    Information you choose to share will only be visible to other users according to your profile visibility setting.
                  </p>
                </div>
              </div>
            )}
            
            {/* Navigation Buttons */}
            <div className="mt-8 flex justify-between">
              {step > 1 ? (
                <button
                  type="button"
                  onClick={handlePrevStep}
                  className="px-4 py-2 border rounded-md text-gray-600 hover:bg-gray-50 transition"
                >
                  Back
                </button>
              ) : (
                <div></div> // Empty div to maintain flex spacing
              )}
              
              {step < totalSteps ? (
                <button
                  type="button"
                  onClick={handleNextStep}
                  className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition flex items-center space-x-2"
                >
                  {saving ? (
                    <>
                      <Loader className="h-5 w-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <Save className="h-5 w-5" />
                      <span>Complete Profile</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileCompletion;